import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { UpdateQuestionChoiceInput } from '__generated__/graphql';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import CustomIcon from 'components/Icon/CustomIcon';
import FormField from 'components/Form/FormField';
import iconDelete from 'assets/icons/iconDelete.svg';
import Stack from '@mui/material/Stack';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'components/StrictModeDroppable/StrictModeDroppable';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { validateQuestionChoices } from 'pages/Activities/ActivityAssignmentStep/StepQuestion/utils/validateQuestion';

type QuestionChoiceEdit = UpdateQuestionChoiceInput & { reactId: string };
type QuestionChoiceOptionalId = UpdateQuestionChoiceInput & { reactId?: string };

function addIdsToChoices(choices: QuestionChoiceOptionalId[]): QuestionChoiceEdit[] {
  return choices.map((choice) => {
    if (choice.reactId) {
      return choice as QuestionChoiceEdit;
    }

    return { ...choice, reactId: choice.id?.toString() || uuidv4() };
  });
}

type ChoiceFormError = {
  reactId: string;
  message: string;
};

function validateQuestionChoicesWithIds(choicesWithIds: QuestionChoiceEdit[]): ChoiceFormError[] {
  const validationErrors = validateQuestionChoices(choicesWithIds);

  return validationErrors.map<ChoiceFormError>((error) => ({
    reactId: choicesWithIds[error.index].reactId,
    message: error.message,
  }));
}

function sortChoices(choices?: QuestionChoiceEdit[]): QuestionChoiceEdit[] {
  if (!choices) {
    return [];
  }

  const sorted = choices.slice();
  sorted.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  return sorted;
}

function reorderChoices(
  choices: QuestionChoiceEdit[],
  startIndex: number,
  endIndex: number
): QuestionChoiceEdit[] {
  const result = Array.from(choices);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result.map((x, index): QuestionChoiceEdit => ({ ...x, sortOrder: index }));
}

interface QuestionChoicesEditProps {
  onChange: (newValues: QuestionChoiceEdit[]) => void;
  value?: QuestionChoiceOptionalId[];
  disabled?: boolean;
}

type Props = QuestionChoicesEditProps;

const QuestionChoicesEdit = ({ onChange, value, disabled }: Props) => {
  const [choicesWithIds, setChoicesWithIds] = useState<QuestionChoiceEdit[]>(() =>
    sortChoices(addIdsToChoices(value || []))
  );

  const formErrors: ChoiceFormError[] = useMemo(
    () => validateQuestionChoicesWithIds(choicesWithIds),
    [choicesWithIds]
  );

  // Reset choicesWithIds if the data is reloaded, and we have choices that don't have a reactId.
  useEffect(() => {
    if (value && !!value.find((x) => !x.reactId)) {
      setChoicesWithIds(sortChoices(addIdsToChoices(value)));
    }
  }, [value]);

  const handleAddNewClick = useCallback(() => {
    const lastSortOrder =
      choicesWithIds.length > 0 ? Math.max(...choicesWithIds.map((x) => x.sortOrder || 0)) : -1;
    const newValue: QuestionChoiceEdit[] = [
      ...choicesWithIds,
      {
        label: '',
        value: '',
        pillarScore: undefined,
        reactId: uuidv4(),
        sortOrder: lastSortOrder + 1,
      },
    ];
    onChange(newValue);
    setChoicesWithIds(newValue);
  }, [choicesWithIds, onChange]);

  const handleOptionTextChange = useCallback(
    (choiceToUpdate: QuestionChoiceEdit, newLabel: string) => {
      const newValue: QuestionChoiceEdit[] = choicesWithIds.map((choiceWithId) => {
        if (choiceWithId.reactId === choiceToUpdate.reactId) {
          return {
            ...choiceWithId,
            label: newLabel,
            value: newLabel,
          };
        }

        return choiceWithId;
      });

      onChange(newValue);
      setChoicesWithIds(newValue);
    },
    [choicesWithIds, onChange]
  );

  const handleDeleteClick = useCallback(
    (choice: QuestionChoiceEdit) => {
      const newValue = choicesWithIds.filter((x) => x.reactId !== choice.reactId);
      onChange(newValue);
      setChoicesWithIds(newValue);
    },
    [choicesWithIds, onChange]
  );

  const onDragEnd = useCallback(
    ({ destination, source }: DropResult) => {
      // dropped outside the list
      if (!destination) {
        return;
      }

      const newValue = reorderChoices(choicesWithIds, source.index, destination.index);
      onChange(newValue);
      setChoicesWithIds(newValue);
    },
    [choicesWithIds, onChange]
  );

  return (
    <Grid container>
      {choicesWithIds && choicesWithIds.length > 0 && (
        <Grid item xs={12} mt={0.5} mb={2.5}>
          <DragDropContext onDragEnd={onDragEnd}>
            <StrictModeDroppable droppableId='droppable-list'>
              {(provided) => (
                <Stack direction='column' ref={provided.innerRef} {...provided.droppableProps}>
                  {choicesWithIds.map((choice, index) => {
                    const fieldError = formErrors.find((x) => x.reactId === choice.reactId);
                    return (
                      <Draggable key={choice.reactId} draggableId={choice.reactId} index={index}>
                        {(provided, snapshot) => (
                          <Grid
                            container
                            alignItems='center'
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              py: 1.5,
                              ...(snapshot.isDragging
                                ? { background: (theme) => theme.colors.offWhite }
                                : undefined),
                            }}
                          >
                            <Grid
                              item
                              xs={1}
                              display='flex'
                              justifyContent='center'
                              alignItems='center'
                            >
                              <CustomIcon icon={<DragIndicatorIcon />} useCursorPointer />
                            </Grid>
                            <Grid item xs={9} md={10}>
                              <FormField
                                field={{
                                  value: choice.label,
                                  containedLabel: true,
                                  sx: { mt: 0 },
                                  fieldProps: {
                                    size: 'small',
                                    placeholder: 'Enter a Response',
                                    required: true,
                                    ...(fieldError && {
                                      error: true,
                                      helperText: fieldError.message,
                                    }),
                                  },
                                }}
                                onChange={(newLabel) => handleOptionTextChange(choice, newLabel)}
                              />
                            </Grid>
                            <Grid item xs={2} md={1} textAlign='right'>
                              <CustomIcon
                                onClick={() => handleDeleteClick(choice)}
                                imgIcon={iconDelete}
                              />
                            </Grid>
                          </Grid>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Stack>
              )}
            </StrictModeDroppable>
          </DragDropContext>
        </Grid>
      )}
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={5}>
            <Button
              type='button'
              variant='outlined'
              onClick={handleAddNewClick}
              disabled={disabled}
            >
              Add New Option
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default QuestionChoicesEdit;
